import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, Modal, Toast, ToastContainer } from 'react-bootstrap';

// ---------------------------------------------------------------- toasts
interface ToastItem {
  id: number;
  kind: 'success' | 'danger' | 'info';
  message: string;
}
interface ToastApi {
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
}
const ToastContext = createContext<ToastApi | null>(null);

// ---------------------------------------------------------------- confirm
export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}
type ConfirmFn = (o: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((kind: ToastItem['kind'], message: string) => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, kind, message }]);
  }, []);
  const toastApi = useMemo<ToastApi>(
    () => ({ success: (m) => push('success', m), error: (m) => push('danger', m), info: (m) => push('info', m) }),
    [push],
  );

  const [pending, setPending] = useState<{ opts: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);
  const confirm = useCallback<ConfirmFn>((opts) => new Promise<boolean>((resolve) => setPending({ opts, resolve })), []);
  const close = (v: boolean) => {
    pending?.resolve(v);
    setPending(null);
  };

  return (
    <ToastContext.Provider value={toastApi}>
      <ConfirmContext.Provider value={confirm}>
        {children}
        <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 2000 }}>
          {toasts.map((t) => (
            <Toast
              key={t.id}
              bg={t.kind}
              autohide
              delay={t.kind === 'danger' ? 8000 : 4000}
              onClose={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
              role={t.kind === 'danger' ? 'alert' : 'status'}
            >
              <Toast.Body className={t.kind === 'info' ? '' : 'text-white'}>{t.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        <Modal show={!!pending} onHide={() => close(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h5">{pending?.opts.title ?? 'Please confirm'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{pending?.opts.message}</Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button variant={pending?.opts.danger === false ? 'primary' : 'danger'} onClick={() => close(true)} autoFocus>
              {pending?.opts.confirmLabel ?? 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(ToastContext);
  if (!v) throw new Error('useToast must be used inside <FeedbackProvider>');
  return v;
}

export function useConfirm(): ConfirmFn {
  const v = useContext(ConfirmContext);
  if (!v) throw new Error('useConfirm must be used inside <FeedbackProvider>');
  return v;
}
